/**
 * Locale Admin API Service — Override APIs
 *
 * Handles communication with the tms-api-locale backend for admin-only
 * (Localization Settings) operations:
 * - Browse locale keys         (GET /keys)            → ADMIN
 * - Batch save overrides       (PUT /override)        → ADMIN
 * - Revert a single override   (DELETE /override/:key)→ ADMIN + JWT
 * - Revert every override       (POST /override/reset-all) → ADMIN
 * - Bulk CSV import            (POST /import)         → ADMIN
 * - Find & replace             (POST /replace/*)      → ADMIN
 * - Sync bundle from tms-types (POST /sync)           → ADMIN
 *
 * These are the "override APIs" used specifically in:
 *   • Settings > Localization > Terminology Editor
 *   • Onboarding > Terminology Review step
 *   • Admin CSV import workflow
 *
 * For default locale APIs (bundle loading, language listing),
 * see locale.service.ts.
 *
 * Backend: tms-api-locale (port 5011)
 * Gateway prefix: /locale → strips prefix → upstream /api/v1/locale/...
 */
import { getApiClient } from '@/core/services/api.service';
import { API_ROUTES } from '@/core/config';
import type {
  LocaleKey,
  LocaleKeysQuery,
  LocaleOverrideBody,
  LocaleOverrideBatchQuery,
  LocaleReplaceApplyBody,
  LocaleReplaceApplyResult,
  LocaleReplacePreviewBody,
  LocaleReplacePreviewResult,
  LocaleSyncBody,
} from '@tms-phoenix/tms-types';

// Re-export types for convenience
export type { LocaleKey, LocaleKeysQuery, LocaleOverrideBody, LocaleOverrideBatchQuery, LocaleSyncBody };

// ── API path ────────────────────────────────────────────────────────────────

/**
 * Full locale API prefix routed through the gateway.
 * Same prefix as locale.service.ts — shared across default + admin APIs.
 */
const LOCALE_API = `${API_ROUTES.LOCALE}/locale`;

/** Standard API response envelope. */
interface _ApiEnvelope<T> {
  success: boolean;
  data: T;
  meta?: {
    requestId: string;
    timestamp: string;
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

/** Response shape for PUT /override. */
interface OverrideResponse {
  processed: number;
  total: number;
  message: string;
  bundle: Record<string, string>;
}

/** Response shape for POST /import. */
export interface ImportResponse {
  processed: number;
  skipped: number;
  total: number;
  message: string;
  bundle: Record<string, string>;
}

/** Response shape for POST /sync. */
export interface SyncResponse {
  tenant_id: string;
  language_code: string;
  is_initial_setup: boolean;
  s3_key: string;
  db_rows_affected: number;
  total_keys: number;
}

// ── Override APIs (Localization Settings) ────────────────────────────────────

/**
 * GET /api/v1/locale/keys
 *
 * Paginated browser over the locale_strings table.
 * ADMIN role required (auth pending on BE).
 *
 * Used by:
 *   • Settings > Localization > Terminology Editor (key list with filters)
 *   • Onboarding > Terminology Review step (pre-filled form)
 *
 * Supports filtering by module, key_type, is_customized, and full-text search.
 *
 * @param query - Filter/pagination params (module, key_type, is_customized, search, page, limit)
 * @returns Paginated array of LocaleKey objects with pagination metadata
 */
export async function fetchLocaleKeys(query: LocaleKeysQuery = {}): Promise<{
  data: LocaleKey[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}> {
  const api = getApiClient();
  const params = new URLSearchParams();

  if (query.module) params.set('module', query.module);
  if (query.key_type) params.set('key_type', query.key_type);
  if (query.is_customized) params.set('is_customized', query.is_customized);
  if (query.search) params.set('search', query.search);
  if (query.lang) params.set('lang', query.lang);
  if (query.page) params.set('page', query.page);
  if (query.limit) params.set('limit', query.limit);

  const url = params.toString()
    ? `${LOCALE_API}/keys?${params.toString()}`
    : `${LOCALE_API}/keys`;

  // createApiClient response interceptor unwraps { success, data, meta }
  // → response.data = the inner `data` array
  // → response.pagination = meta.pagination (attached by interceptor)
  const response = await api.get(url);

  const pagination = (response as unknown as Record<string, unknown>).pagination as
    { page: number; limit: number; total: number; totalPages: number } | undefined;

  return {
    data: (response.data ?? []) as LocaleKey[],
    pagination: pagination ?? { page: 1, limit: 50, total: 0, totalPages: 0 },
  };
}

/**
 * PUT /api/v1/locale/override?lang=<code>
 *
 * Batch-saves 1–500 terminology overrides for the caller's tenant and the
 * given language. Tenant is resolved from the caller's session on the
 * backend; `tenantId` is accepted but ignored by the handler — kept for
 * backward compatibility with other apps sharing this common API.
 *
 * Each key calls upsert_locale_override() atomically — sets custom_value
 * and appends an OVERRIDE audit row. After all upserts, the full resolved
 * bundle is rebuilt from the DB and written to S3. CloudFront is invalidated.
 *
 * Used by:
 *   • Settings > Localization > Terminology Editor (save changes)
 *   • Onboarding > Terminology Review step (submit overrides)
 *
 * After a successful call, the caller should call i18next.reloadResources()
 * so the admin sees the updated terms immediately.
 *
 * @param query - lang (language code), tenantId (optional, backward-compat only)
 * @param body  - { overrides: [{ key, custom_value, custom_value_plural? }] }
 * @returns Processed count, total, message, and the updated full bundle
 */
export async function saveOverrides(
  query: Pick<LocaleOverrideBatchQuery, 'lang' | 'tenantId'>,
  body: LocaleOverrideBody,
): Promise<OverrideResponse> {
  const api = getApiClient();
  const params = new URLSearchParams({
    lang: query.lang,
    ...(query.tenantId ? { tenantId: query.tenantId } : {}),
  });

  // createApiClient's response interceptor already unwraps { success, data, meta }
  // → response.data IS the inner payload (OverrideResponse)
  const response = await api.put<OverrideResponse>(
    `${LOCALE_API}/override?${params.toString()}`,
    body,
  );

  return response.data;
}

/**
 * DELETE /api/v1/locale/override/:key?tenantId=&lang=
 *
 * Reverts a terminology override, restoring resolved_value to
 * translated_value (or base_value if no translation exists).
 *
 * Calls reset_locale_override() which clears custom_value and appends a
 * RESET audit row, then — same as saveOverrides — rebuilds the full
 * resolved bundle from the DB and writes it to S3 for this tenant+language.
 *
 * Used by:
 *   • Settings > Localization > Terminology Editor (reset single key)
 *   • Key inspector popover (restore default)
 *
 * After a successful call, the caller should apply the returned bundle
 * directly to the live i18next instance (see applyFreshBundle) rather than
 * re-fetching it — same reasoning as saveOverrides.
 *
 * @param key   - The translation key to reset (e.g. 'label.feed')
 * @param query - tenantId (UUID) + lang (language code)
 * @returns Processed count, total, message, and the updated full bundle
 */
export async function deleteOverride(key: string, query: LocaleOverrideBatchQuery): Promise<OverrideResponse> {
  const api = getApiClient();
  const params = new URLSearchParams({
    tenantId: query.tenantId,
    lang: query.lang,
  });

  const response = await api.delete<OverrideResponse>(
    `${LOCALE_API}/override/${encodeURIComponent(key)}?${params.toString()}`,
  );

  return response.data;
}

/**
 * POST /api/v1/locale/replace/preview?tenantId=&lang=
 *
 * Read-only: every key whose translation contains the find term, matched
 * with the shared rules in @tms-phoenix/tms-types' find-replace (placeholders
 * skipped, Unicode/locale-aware case and word boundaries). Capped at
 * LOCALE_REPLACE_MAX_KEYS rows — `truncated` says when there were more.
 *
 * Used by: Settings > Localization > Find & replace dialog
 */
export async function previewReplace(
  query: LocaleOverrideBatchQuery,
  body: LocaleReplacePreviewBody,
): Promise<LocaleReplacePreviewResult> {
  const api = getApiClient();
  const params = new URLSearchParams({ tenantId: query.tenantId, lang: query.lang });
  const response = await api.post<LocaleReplacePreviewResult>(
    `${LOCALE_API}/replace/preview?${params.toString()}`,
    body,
  );
  return response.data;
}

/**
 * POST /api/v1/locale/replace/apply?tenantId=&lang=
 *
 * Replaces the term in the given keys in one transaction (the server
 * re-checks each key against its current value), regenerates the S3 bundle
 * once, and returns that bundle.
 *
 * Used by: Settings > Localization > Find & replace dialog
 */
export async function applyReplace(
  query: LocaleOverrideBatchQuery,
  body: LocaleReplaceApplyBody,
): Promise<LocaleReplaceApplyResult> {
  const api = getApiClient();
  const params = new URLSearchParams({ tenantId: query.tenantId, lang: query.lang });
  const response = await api.post<LocaleReplaceApplyResult>(
    `${LOCALE_API}/replace/apply?${params.toString()}`,
    body,
  );
  return response.data;
}

/**
 * POST /api/v1/locale/override/reset-all?tenantId=&lang=
 *
 * Reverts EVERY customized key to its standard translation in one
 * transaction (a RESET audit row per key), then rebuilds the bundle once —
 * instead of one DELETE /override/:key per key. Returns `processed` (how
 * many were reset) and the fresh bundle, same as deleteOverride.
 *
 * Used by: Settings > Localization > Customized Translations > Restore All To Default
 */
export async function resetAllOverrides(query: LocaleOverrideBatchQuery): Promise<OverrideResponse> {
  const api = getApiClient();
  const params = new URLSearchParams({ tenantId: query.tenantId, lang: query.lang });
  const response = await api.post<OverrideResponse>(`${LOCALE_API}/override/reset-all?${params.toString()}`);
  return response.data;
}

/**
 * POST /api/v1/locale/import?language_code=<code>
 *
 * Bulk-imports up to 500 terminology overrides from a CSV file upload.
 * Tenant is resolved from the caller's session on the backend; `tenant_id`
 * is accepted but ignored by the handler — kept for backward compatibility
 * with other apps sharing this common API. ADMIN role required (auth pending on BE).
 *
 * Only keys already present in locale_strings are updated — unknown keys
 * are silently skipped. After all rows are processed, the full resolved
 * bundle is rebuilt and S3 is updated. CloudFront is invalidated.
 *
 * Used by:
 *   • Settings > Localization > CSV Import dialog
 *   • Onboarding > Bulk terminology import
 *
 * @param tenantId     - Ignored by the handler; kept optional for backward compatibility
 * @param languageCode - Language code (e.g. 'en', 'en-CA')
 * @param file         - CSV File object (multipart/form-data, field name: 'file')
 * @returns Processed, skipped, total counts, message, and updated bundle
 */
export async function importOverrides(
  tenantId: string | undefined,
  languageCode: string,
  file: File,
): Promise<ImportResponse> {
  const api = getApiClient();
  const params = new URLSearchParams({
    language_code: languageCode,
    ...(tenantId ? { tenant_id: tenantId } : {}),
  });

  const formData = new FormData();
  formData.append('file', file);

  // createApiClient's response interceptor already unwraps { success, data, meta }
  // → response.data IS the inner payload (ImportResponse)
  const response = await api.post<ImportResponse>(
    `${LOCALE_API}/import?${params.toString()}`,
    formData,
  );

  return response.data;
}

/**
 * POST /api/v1/locale/sync
 *
 * Syncs a language bundle from @tms-phoenix/tms-types into S3 and the
 * locale_strings table. ADMIN role required (auth pending on BE).
 *
 * Behaviour depends on is_initial_setup:
 *   • true  — INSERT new rows (first-time onboarding or adding a new language)
 *   • false — UPDATE translated_value on existing rows (refresh after tms-types update)
 *
 * Used by:
 *   • Onboarding > Language selection (initial tenant setup)
 *   • Admin > Sync translations after a tms-types bundle update
 *
 * @param body - { language_code, is_initial_setup }
 * @returns Sync result with S3 key, rows affected, and total keys
 */
export async function syncBundle(body: LocaleSyncBody): Promise<SyncResponse> {
  const api = getApiClient();
  // createApiClient's response interceptor already unwraps { success, data, meta }
  // → response.data IS the inner payload (SyncResponse)
  const response = await api.post<SyncResponse>(
    `${LOCALE_API}/sync`,
    body,
  );
  return response.data;
}
